<script lang="ts">
	import type { PoiData } from '$lib/components/pois/types';
	import { page } from '$app/state';
	import { computeSearchParamsItinerary } from '$lib/components/itinerary/util';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import { usePopupState } from '$lib/components/map/popup/usePopupState.svelte';
	import { buildPoiCardProps } from '$lib/components/pois/card/factories';
	import PoiCard from '$lib/components/pois/card/PoiCard.svelte';
	import { getCardHeight } from '$lib/components/pois/list/utils';
	import { getPoiDatasheetRoute, getPoiDetailLink } from '$lib/components/pois/utils';
	import {
		trackAddItineraryEvent,
		trackPoiListBookEvent,
	} from '$lib/services/tracking/googleAnalytics/pois';
	import { isMapPoiListPage } from '$lib/services/urls/routeIds';
	import { getSearchParamsStore, gotoWithParams } from '$lib/services/urls/searchParams';
	import { isVanitySearchParams } from '$lib/services/urls/vanity';
	import { type Offset, Popup } from 'svelte-maplibre';
	import { get } from 'svelte/store';

	interface Props {
		onclick: (poi: PoiData) => void;
	}

	const { onclick }: Props = $props();

	const DEFAULT_OFFSET: Offset = 16;
	const { getPopupState, setPopupState } = usePopupState();
	const mapLayoutContext = getMapLayoutContext();
	const { hoveredPoiId, itineraryStore } = mapLayoutContext;
	const searchParams = getSearchParamsStore();

	const poi = $derived(getPopupState().poi);
	const open = $derived(getPopupState().open);

	const cardProps = $derived.by(() => {
		if (!poi) return undefined;

		const datas = buildPoiCardProps(
			poi as PoiData,
			buildPoiDetailLink(),
			0,
			getCardHeight(poi as PoiData),
			{ tag: 'div', classes: 'w-168px! bg-white after:content-none!' },
		);

		if (datas?.footer?.link?.href) {
			datas.footer.link.href = datas.footer.link.href.replace('poilist', 'poimap');
		}

		return datas;
	});

	function handlePopupMouseEnter() {
		setPopupState({ activePoiId: poi?.id, open: true, poi });
		$hoveredPoiId = poi?.id ?? null;
	}

	function handlePopupMouseLeave() {
		setPopupState({ activePoiId: undefined, open: false, poi: undefined });
	}

	function handleCardClick(e: MouseEvent, poi: PoiData) {
		e.preventDefault();

		const target = e.target as HTMLElement;
		const isCarouselControlsButtonClick
			= target instanceof HTMLButtonElement
				|| target?.parentElement?.parentElement instanceof HTMLButtonElement
				|| target.parentElement instanceof HTMLButtonElement;

		if (isCarouselControlsButtonClick) {
			return;
		}

		$hoveredPoiId = null;
		setPopupState({ activePoiId: undefined, open: false, poi: undefined });
		onclick?.(poi as PoiData);
	}

	function buildPoiDetailLink() {
		const datasheetRoute = getPoiDatasheetRoute(page.route, poi as PoiData);
		const withSearchParams = isMapPoiListPage(page.route) && !($searchParams.poiCategories?.length === 1 && isVanitySearchParams(page.url));
		return getPoiDetailLink(poi as PoiData, $searchParams, datasheetRoute, withSearchParams);
	}

	async function handleAddItineraryClick(poi: PoiData) {
		if (!poi || !itineraryStore) return;

		trackAddItineraryEvent(poi, 'poimap_itinerary_button');

		itineraryStore.addToItinerary({ coordinates: poi.coordinates, label: poi.name });
		const itinerary = await computeSearchParamsItinerary(get(itineraryStore.itinerary));

		$hoveredPoiId = null;
		setPopupState({ activePoiId: undefined, open: false, poi: undefined });
		gotoWithParams('/routes', {
			...$searchParams,
			itinerary,
		});
	}

	function handleBookClick(poi: PoiData) {
		trackPoiListBookEvent(poi, 'poimap_book');
	}
</script>

<Popup
	open={open && !!poi}
	offset={DEFAULT_OFFSET}
	lngLat={poi ? [poi.coordinates.lng, poi.coordinates.lat] : undefined}
	onclose={handlePopupMouseLeave}
	onhover={handlePopupMouseEnter}
	focusAfterOpen={false}
	canOpen={() => $hoveredPoiId === getPopupState().activePoiId && !!poi}
	popupClass="!bg-transparent !border-color-transparent !shadow-none rd-4 overflow-hidden"
>
	<PoiCard
		{cardProps}
		onCardClick={e => handleCardClick(e, poi as PoiData)}
		onAddItineraryClick={() => handleAddItineraryClick(poi as PoiData)}
		onBookClick={() => handleBookClick(poi as PoiData)}
		onmouseenter={handlePopupMouseEnter}
		onmouseleave={handlePopupMouseLeave}
	/>
</Popup>

<style>
	:global(.maplibregl-popup-content) {
		background: transparent !important;
		border: none !important;
		box-shadow: none !important;
	}
	:global(.maplibregl-popup-tip) {
		background: transparent !important;
		border-color: transparent !important;
		box-shadow: none !important;
	}
	:global(.maplibregl-popup-content .poi-card.xs) {
		height: 124px;
	}
	:global(.maplibregl-popup-content .poi-card.sm) {
		height: 196px;
	}
	:global(.maplibregl-popup-content .poi-card.md) {
		height: 242px;
	}
	:global(.maplibregl-popup-content .poi-card.lg) {
		height: 276px;
	}

	:global(.maplibregl-popup-content .poi-card.xl) {
		height: 328px;
	}

	:global(.maplibregl-popup-content .poi-card-hero .image) {
		max-height: 132px;
	}
</style>
